import { TRACKS, STAGES, MEASURES, PACK_ARTIFACTS } from "./data";
import { FileText, BadgeCheck, ShieldCheck } from "lucide-react";

export function TracksSection() {
  return (
    <section id="tracks" className="border-t border-[var(--border)] bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline">// 02_WORKFORCE</div>
            <h2 className="font-display font-bold text-4xl uppercase mt-3">Four tracks. One mission.</h2>
            <p className="mt-4 text-sm text-[var(--text-2)] max-w-xl">
              Buy seats by role — operators on the SOC, engineers building the platform, hunters chasing
              intrusions, and leaders running the squad. Each track has its own certification board and rubric.
            </p>
          </div>
          <div className="font-mono text-xs text-[var(--text-2)] tracking-widest hidden md:block">04 ROLES &middot; 01 MISSION</div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {TRACKS.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.id} className="flat-card p-8 relative" data-testid={`track-card-${t.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Icon size={28} strokeWidth={1.5} color={t.color} />
                    <div>
                      <div className="font-mono text-xs tracking-widest text-[var(--text-2)]">{t.tag}</div>
                      <h3 className="font-display font-bold text-2xl uppercase">{t.name}</h3>
                    </div>
                  </div>
                  <span className="tag" style={{ color: t.color, borderColor: t.color }}>{t.line}</span>
                </div>
                <p className="mt-5 text-[var(--text-2)]">{t.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ModelSection() {
  return (
    <section id="model" className="border-t border-[var(--border)]">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="overline">// 03_LIFECYCLE</div>
        <h2 className="font-display font-bold text-4xl uppercase mt-3">Foundation to recertification — managed.</h2>
        <p className="mt-4 text-sm text-[var(--text-2)] max-w-2xl mb-12">
          Five stages, one continuous loop. Recertification auto-enrolls trainees the moment a credential
          enters its expiry window — your readiness reports never have a gap.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[var(--border)] border border-[var(--border)]">
          {STAGES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="bg-[var(--bg)] p-6 group hover:bg-[var(--surface)] transition-colors" data-testid={`stage-${s.n}`}>
                <div className="flex items-center justify-between mb-4">
                  <Icon size={22} strokeWidth={1.5} color="var(--cyan)" />
                  <span className="font-mono text-xs text-[var(--text-2)]">/ STG-{s.n}</span>
                </div>
                <div className="font-display text-xl font-bold uppercase">{s.name}</div>
                <p className="text-sm text-[var(--text-2)] mt-2">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** What's inside the on-demand compliance pack PDF — the headline B2B artifact. */
export function EvidenceSection() {
  return (
    <section id="evidence" className="border-t border-[var(--border)] bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="overline">// 04_EVIDENCE</div>
          <h2 className="font-display font-bold text-4xl uppercase mt-3">
            One PDF. Every<br/>question answered.
          </h2>
          <p className="mt-6 text-[var(--text-2)] leading-relaxed">
            The compliance pack is the artifact procurement officers and tender evaluators actually ask for —
            rendered on demand from live training data, signed, and verifiable.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            <li className="flex gap-3"><ShieldCheck size={16} color="var(--green)" strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span><span className="text-white">ISM control mapping</span> — every certification tagged to ASD ISM controls and Essential&nbsp;Eight maturity levels.</span>
            </li>
            <li className="flex gap-3"><BadgeCheck size={16} color="var(--cyan)" strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span><span className="text-white">Public verify URLs</span> — every badge listed with its cryptographic verify link.</span>
            </li>
            <li className="flex gap-3"><FileText size={16} color="var(--amber)" strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span><span className="text-white">AI-graded practicals</span> — defensible scores with strengths &amp; weaknesses on the page.</span>
            </li>
          </ul>
        </div>

        <div className="md:col-span-7">
          <div className="flat-card p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg)]">
              <div className="flex items-center gap-2">
                <FileText size={14} color="var(--cyan)" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">// DEFDOSSIER_COMPLIANCE_PACK · operator.pdf</div>
              </div>
              <div className="flex gap-1">
                {["#FF3B30", "#FFB000", "#00FF41"].map((c) => (
                  <span key={c} className="block w-2 h-2 rounded-full" style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="p-6 font-mono text-xs">
              <div className="overline mb-4">// EVIDENCE_SUMMARY · 2026-02</div>
              <table className="w-full" data-testid="evidence-table">
                <tbody>
                  {PACK_ARTIFACTS.map((row) => (
                    <tr key={row.label} className="border-b border-[var(--border)] last:border-0">
                      <td className="py-2 pr-4 text-[var(--text-2)] tracking-widest uppercase">{row.label}</td>
                      <td className="py-2 text-white">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-5 flex items-center gap-2 text-[10px] text-[var(--text-2)]">
                <span className="text-[var(--green)] blink">█</span>
                <span>defdossier-compliance-pack-maj-arjun-verma.pdf · signed · {new Date().toISOString().slice(0, 10)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function MeasuresSection() {
  return (
    <section id="metrics" className="border-t border-[var(--border)]">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="overline">// 05_OUTCOMES</div>
        <h2 className="font-display font-bold text-4xl uppercase mt-3 mb-3">Outcomes your CFO understands.</h2>
        <p className="text-sm text-[var(--text-2)] max-w-2xl mb-12">
          Audit prep collapses from weeks to a single API call. Recertification slippage drops to zero.
          Cert pass rates and exercise performance show up in the same dashboard as the compliance pack.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {MEASURES.map((m) => (
            <div key={m.k} className="flat-card p-6" data-testid={`measure-${m.k.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-mono text-4xl text-[var(--cyan)] cyan-text-glow">{m.v}</div>
              <div className="font-display uppercase text-lg mt-3">{m.k}</div>
              <div className="text-sm text-[var(--text-2)] mt-2">{m.d}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 flat-card p-6 font-mono text-sm text-[var(--text-2)]">
          <span className="text-[var(--green)]">$</span> cda readiness --report --tenant=acme-defence
          <div className="mt-2 text-white">incident_response_effectiveness = <span className="text-[var(--cyan)]">88%</span> &middot; mean_time_to_contain = <span className="text-[var(--cyan)]">14m</span> &middot; range_pass_rate = <span className="text-[var(--cyan)]">92%</span></div>
          <div className="mt-1 text-white">audit_prep_hours_saved = <span className="text-[var(--cyan)]">240</span> &middot; recert_slippage = <span className="text-[var(--green)]">0</span></div>
          <div className="mt-1">
            <span className="text-[var(--green)] blink">█</span>
          </div>
        </div>
      </div>
    </section>
  );
}
